<script setup>
import { useAxios } from '@/hooks/useAxios';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, nextTick, onMounted, ref, watch } from 'vue';
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const insuranceData = ref(null);
const isReadonly = computed(() => props.readonly);
const isVisible = computed(() => props.visible);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isPopupVisible = ref(false);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};
const searchQuery = ref('');
const filteredOptions = ref([]);

const filterOptions = () => {
    filteredOptions.value = insuranceData.value.filter((option) => option.text.toLowerCase().includes(searchQuery.value.toLowerCase()));
};

onMounted(async () => {
    await Promise.all([searchInsurance()]);
    nextTick(() => {
        filteredOptions.value = insuranceData.value;
    });
});

const searchInsurance = async () => {
    const config = { method: 'get' };
    try {
        const { execute } = useAxios('/api/insurance/com', config, {
            immediate: false,
            onSuccess(response) {
                // console.log('response.data.data!!!!');
                // console.log(response.data.data);

                const newList = response.data.data.map((item) => ({
                    value: item.insurance_id,
                    text: item.insurance_name,
                    id: item.insurance_id,
                    collision_coverage_type: item.collision_coverage_type, // 자차한도 비율
                    bodily_injury_coverage: item.bodily_injury_coverage,
                    bodily_injury_excess: item.bodily_injury_excess,
                    insurance_id: item.insurance_id,
                    insurance_name: item.insurance_name,
                    insurance_state: item.insurance_state,
                    insurance_type: item.insurance_type,
                    personal_death_coverage: item.personal_death_coverage,
                    personal_injury_coverage: item.personal_injury_coverage,
                    personal_injury_excess: item.personal_injury_excess,
                    property_damage_coverage: item.property_damage_coverage,
                    property_damage_excess: item.property_damage_excess
                }));
                insuranceData.value = newList;
            },
            onError(err) {
                console.error('Error:', err.message);
                insuranceData.value = [];
            }
        });
        await execute();
    } catch (err) {
        console.error(err);
        insuranceData.value = [];
    }
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value);

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value) => {
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        value: value,
        vehicle_id: '',
        model: '',
        fuel_type: '',
        mileage: 0
    });
};

const togglePopup = () => {
    isPopupVisible.value = !isPopupVisible.value;
};

const closePopup = () => {
    isPopupVisible.value = false;
};

const selectValue = (option) => {
    inputValue.value = option.value;

    emit('update:modelValue', option.insurance_name);
    emit('input', {
        id: props.inputId,
        value: option.value,
        insurance_name: option.insurance_name || '',
        bodily_injury_coverage: option.bodily_injury_coverage || 0,
        bodily_injury_excess: option.bodily_injury_excess || 0,
        property_damage_coverage: option.property_damage_coverage || 0,
        property_damage_excess: option.property_damage_excess || 0,
        personal_injury_coverage: option.personal_injury_coverage || 0,
        personal_death_coverage: option.personal_death_coverage || 0,
        personal_injury_excess: option.personal_injury_excess || 0
    });

    ValidationMsg.value = '';
    closePopup();
};

const clearInput = () => {
    emitUpdate('', 'input');
};

const getDefaultInsurance = async () => {
    if (!Array.isArray(insuranceData.value) || insuranceData.value.length === 0) {
        await searchInsurance();
    }

    const list = (insuranceData.value || []).filter((i) => i.insurance_state === 'Y');
    const option = list[0];
    if (!option) return null;

    return {
        id: props.inputId,
        value: option.value,
        insurance_name: option.insurance_name || '',
        bodily_injury_coverage: option.bodily_injury_coverage || 0,
        bodily_injury_excess: option.bodily_injury_excess || 0,
        property_damage_coverage: option.property_damage_coverage || 0,
        property_damage_excess: option.property_damage_excess || 0,
        personal_injury_coverage: option.personal_injury_coverage || 0,
        personal_death_coverage: option.personal_death_coverage || 0,
        personal_injury_excess: option.personal_injury_excess || 0
    };
};

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});

const numberFormatter = (num) => {
    let value = num;
    if (typeof value === 'number') {
        value = value.toLocaleString(); // 숫자에 천 단위 콤마 추가
    } else if (typeof value === 'string' && !isNaN(value)) {
        value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ','); // 문자열로 숫자일 경우 정규식으로 콤마 추가
    } else {
        value = value; // 숫자가 아니면 원래 값 그대로
    }
    return value;
};

defineExpose({
    getDefaultInsurance
});
</script>

<template>
    <!-- 불러오기는 Readonly값일경우에는 표출 하지않도록 한다. -->
    <div
        v-if="!isReadonly && isVisible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="!labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center w-full h-12">
            <button @click="togglePopup" class="flex-1 px-4 h-[2.3rem] text-[0.875rem] rounded-md border border-blue-500 bg-blue-50 text-blue-700 hover:bg-blue-100 active:bg-blue-200 transition-colors duration-150 font-semibold">
                종합보험 선택
            </button>
        </div>

        <Teleport to="body">
            <div v-if="isPopupVisible" class="popup-overlay">
                <div class="popup-content">
                    <!-- <h3>선택</h3> -->
                    <!-- <input v-model="searchQuery" @input="filterOptions" placeholder="자차보험을 검색하세요" class="search-input" style="margin-top: 0.5rem" /> -->
                    <div class="options-container">
                        <table class="cominsurance-grid">
                            <thead>
                                <tr>
                                    <th>종합보험명</th>
                                    <th>대인한도</th>
                                    <th>대물한도</th>
                                    <th>대인 면책금(인당)</th>
                                    <th>대물한도</th>
                                    <th>대물 면책금(건당)</th>
                                    <th>자손 한도(부상)</th>
                                    <th>자손 한도(사망)</th>
                                    <th>자손 면책금</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="option in filteredOptions" :key="option.value" @click="selectValue(option)" class="popup-item">
                                    <td>{{ option.insurance_name }}</td>
                                    <td>
                                        {{ option.bodily_injury_coverage && option.bodily_injury_coverage.toString() === '-1' ? '무한' : `${numberFormatter(option.bodily_injury_coverage)}만원` }}
                                    </td>
                                    <td>{{ numberFormatter(option.property_damage_coverage) }} 만원</td>
                                    <td>{{ numberFormatter(option.bodily_injury_excess) }} 만원</td>
                                    <td>{{ numberFormatter(option.property_damage_coverage) }} 만원</td>
                                    <td>{{ numberFormatter(option.property_damage_excess) }} 만원</td>
                                    <td>{{ numberFormatter(option.personal_injury_coverage) }} 만원</td>
                                    <td>{{ numberFormatter(option.personal_death_coverage) }} 만원</td>
                                    <td>{{ numberFormatter(option.personal_injury_excess) }} 만원</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <button @click="closePopup" class="close-button">닫기</button>
                </div>
            </div>
        </Teleport>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.cominsurance-grid th {
    position: sticky;
    top: 0;
    background-color: #f2f2f2;
    z-index: 10;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171; /* 빨간색 */
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
}

.alert-box {
    padding: 0.5rem 1rem;
    background: red;
    color: white;
    border-radius: 0.25rem;
    margin-bottom: 0.5rem;
    text-align: center;
}
.popup-item.disabled {
    opacity: 0.5;
    pointer-events: none;
    cursor: not-allowed;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    user-select: none;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}
.popup-content {
    background: #fff;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-bottom: 1rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: 70rem;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    text-align: left;
    min-width: 30rem;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 12rem;
    max-height: 25rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.cominsurance-grid {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.cominsurance-grid th,
.cominsurance-grid td {
    border: 0.1rem solid #ddd;
    padding: 0.3rem;
    font-family: 'SCoreDream300';
    font-size: 0.875rem;
}

.cominsurance-grid th {
    background-color: #f2f2f2;
    text-align: center;
}

.cominsurance-grid td {
    text-align: left;
    padding: 0.3rem 1rem;
}

.cominsurance-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.cominsurance-grid th:hover {
    cursor: default;
}

.cominsurance-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
